import axios from 'axios';
import type { 
  MiniQuizQuestion, 
  GetQuestionsParams, 
  MiniQuizStatistics, 
  MiniQuizFormData 
} from '@/types';
import { API_CONFIG } from '../config/apiConfig';

const miniQuizApi = axios.create({
  baseURL: API_CONFIG.adminBaseURL,
  timeout: API_CONFIG.defaultTimeout,
});

// Request interceptor for adding auth headers
miniQuizApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminAccessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
miniQuizApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminAccessToken');
      localStorage.removeItem('adminRefreshToken');
      localStorage.removeItem('adminUser');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface MiniQuizResponse {
  questions: MiniQuizQuestion[];
  total: number;
  page: number;
  limit: number;
}

export interface UploadImageResponse {
  filename: string;
  originalName: string;
  size: number;
  mimetype: string;
  url: string;
}

class MiniQuizService {
  private readonly baseUrl = '/quizzes';

  // Get all questions with pagination and filters
  async getQuestions(filters: GetQuestionsParams = {}): Promise<MiniQuizResponse> {
    const params = new URLSearchParams();
    
    if (filters.page) params.set('page', filters.page.toString());
    if (filters.limit) params.set('limit', filters.limit.toString());
    if (filters.search) params.set('search', filters.search);
    if (filters.isActive !== undefined) params.set('isActive', filters.isActive.toString());
    if (filters.category) params.set('category', filters.category);
    if (filters.sortBy) params.set('sortBy', filters.sortBy);
    if (filters.sortOrder) params.set('sortOrder', filters.sortOrder);

    const queryString = params.toString();
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
    
    const response = await miniQuizApi.get<{success: boolean, data: any}>(url);
    
    // Transform server response to match client interface
    const serverData = response.data.data;
    return {
      questions: serverData.questions,
      total: serverData.pagination.totalQuestions,
      page: serverData.pagination.currentPage,
      limit: serverData.pagination.limit,
    };
  }

  // Get question by ID
  async getQuestion(id: number): Promise<MiniQuizQuestion> {
    const response = await miniQuizApi.get<{success: boolean, data: MiniQuizQuestion}>(`${this.baseUrl}/${id}`);
    return response.data.data;
  }

  // Create new question
  async createQuestion(data: MiniQuizFormData): Promise<MiniQuizQuestion> {
    const response = await miniQuizApi.post<{success: boolean, data: MiniQuizQuestion}>(this.baseUrl, data);
    return response.data.data;
  }

  // Update question
  async updateQuestion(id: number, data: Partial<MiniQuizFormData>): Promise<MiniQuizQuestion> {
    const response = await miniQuizApi.put<{success: boolean, data: MiniQuizQuestion}>(`${this.baseUrl}/${id}`, data);
    return response.data.data;
  }

  // Delete question
  async deleteQuestion(id: number): Promise<void> {
    await miniQuizApi.delete(`${this.baseUrl}/${id}`);
  }

  // Bulk update status
  async bulkUpdateStatus(questionIds: number[], isActive: boolean): Promise<void> {
    await miniQuizApi.patch(`${this.baseUrl}/bulk-status`, {
      questionIds,
      isActive,
    });
  }

  // Get statistics
  async getStatistics(): Promise<MiniQuizStatistics> {
    const response = await miniQuizApi.get<{success: boolean, data: MiniQuizStatistics}>(`${this.baseUrl}/statistics`);
    return response.data.data;
  }

  // Get available categories
  async getCategories(): Promise<string[]> {
    const response = await miniQuizApi.get<{success: boolean, data: {categories: string[]}}>(`${this.baseUrl}/categories`);
    return response.data.data.categories;
  }

  // Export questions
  async exportQuestions(format: 'csv' | 'json' | 'xlsx' = 'csv', filters?: { isActive?: boolean }): Promise<{
    totalRecords: number;
    exportedAt: string;
    format: string;
    filters: any;
    downloadUrl: string;
    expiresAt: string;
  }> {
    const params = new URLSearchParams();
    params.set('format', format);
    if (filters?.isActive !== undefined) params.set('isActive', filters.isActive.toString());
    
    const response = await miniQuizApi.post<{success: boolean, data: any}>(`${this.baseUrl}/export?${params.toString()}`);
    return response.data.data;
  }

  // Upload image
  async uploadImage(file: File): Promise<UploadImageResponse> {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await miniQuizApi.post<{success: boolean, data: UploadImageResponse}>(`${this.baseUrl}/upload-image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  }
}

export const miniQuizService = new MiniQuizService();
